import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { licensePlateRecognitionService } from '../../../services/central-api/license-plate-recognition';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { LPRParamList } from '../../../typings/Params';
import ReportModal from './ReportModal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import ReportCountModal from './ReportCountModal';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { useCurrentApp } from '../../../context/CurrentApp';
import { RootNavigation } from '../../../Routes';

export default function Reports({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<LPRParamList, 'LprReports'>>();
    const { isParentApp } = useCurrentApp();

    // filters
    const [filters, setFilters] = useState<{ page: number; textFilter?: string; limit: number; }>({
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [totalItems, setTotalItems] = useState(0);

    const [reports, setReports] = useState<PlateDetectionsReport[]>([]);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isDownloading, setIsDownloading] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [isParentModalVisible, setParentModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['lprReports', filters],
        queryFn: () => licensePlateRecognitionService.getReports(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setReports(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleFormClose() {
        setModalVisible(false);
        refetch();
    }

    function handleFilters() {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    return <Container>
        <Header
            title={translate('reports')}
            actions={<>
                {isParentApp && <CustomButton type='secondary' text={translate('detectionsCountReport')} onPress={() => setParentModalVisible(true)} />}
                <CustomButton text={translate('detectionsReport')} onPress={() => setModalVisible(true)} />
            </>}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredReports')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true
                    },
                    {
                        name: translate('user'),
                        selector: row => row.user?.name || '',
                        wrap: true
                    },
                    {
                        name: 'Token',
                        selector: row => row.token,
                        wrap: true
                    },
                    {
                        name: translate('status'),
                        cell: row =>
                            <View style={{ flexDirection: 'row', columnGap: 5, alignItems: 'center', width: '100%' }}>
                                {row.status == 'pending' ?
                                    <ActivityIndicator size={16} color={theme.colors.iconColor} /> :
                                    <FontAwesomeIcon style={styles.statusStyle(row.status)} fontSize={16} icon={row.status == 'success' ? faCheckCircle : faTimesCircle}></FontAwesomeIcon>
                                }
                            </View>
                    },
                    {
                        name: translate('filters'),
                        wrap: true,
                        cell: row => <View style={{ width: '100%' }}>
                            {row.type == 'plate_detections' ?
                                <View style={{ flexDirection: 'column', rowGap: 5 }}>
                                    <MyAppText>{translate('start')}: {moment(row.filters.startDate).format('DD/MM/YYYY HH:mm')}</MyAppText>
                                    <MyAppText>{translate('end')}: {moment(row.filters.endDate).format('DD/MM/YYYY  HH:mm')}</MyAppText>
                                    {row.filters.type ? <MyAppText>{translate('type')}: {row.filters.type}</MyAppText> : undefined}
                                    {row.filters.plate ? <MyAppText>{translate('plate')}: {row.filters.plate}</MyAppText> : undefined}
                                    {row.filters.brand ? <MyAppText>{translate('brand')}: {row.filters.brand}</MyAppText> : undefined}
                                    {row.filters.model ? <MyAppText>{translate('model')}: {row.filters.model}</MyAppText> : undefined}
                                    {row.filters.color ? <MyAppText>{translate('color')}: {row.filters.color}</MyAppText> : undefined}
                                    {row.filters.year ? <MyAppText>{translate('year')}: {row.filters.year}</MyAppText> : undefined}
                                    {row.filters.camerasIds?.length ? <MyAppText>{translate('cameras')}: {row.filters.camerasIds}</MyAppText> : undefined}
                                    {row.filters.unitNames?.length ? <MyAppText>{translate('teams')}: {row.filters.unitNames}</MyAppText> : undefined}
                                </View> : <></>}
                        </View>
                    },
                    {
                        name: translate('createdAt'),
                        selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                    },
                    {
                        button: true,
                        cell: row =>
                            <TouchableOpacity disabled={isDownloading} onPress={async () => {
                                try {
                                    setIsDownloading(true);
                                    const video = await licensePlateRecognitionService.getReport(row.id);
                                    window.open(video.resultUrl);
                                } catch (error) {
                                    Toast.show({
                                        type: 'sentinelxError',
                                        text1: translate('unexpectedError'),
                                    });
                                } finally {
                                    setIsDownloading(false);
                                }
                            }}>
                                {row.status === 'success' ? <FontAwesomeIcon fontSize={16} color={theme.colors.iconColor} icon={faDownload}></FontAwesomeIcon> : null}
                            </TouchableOpacity>
                    }
                    ]}
                    data={reports}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={totalItems}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />

        </Card>

        <ReportModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            onClose={handleFormClose}
        />

        <ReportCountModal
            isModalVisible={isParentModalVisible}
            setModalVisible={setParentModalVisible}
        />
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    statusStyle: (status: VideoAnalysisStatus) => {
        return {
            color: status === 'success' ? theme.colors.successes['500'] : theme.colors.errors['500']
        };
    },
}));
